import { useAuth } from "@/hooks/AuthContext";
import { useProjects } from "@/hooks/Projects";
import React, { useEffect, useMemo, useState } from "react";
import api from "@/api/axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { API, fmtDate } from "@/App";
import { KanbanBoard } from "@/components/ui/KanbanBoard";
import {useParams} from "react-router-dom";

function TaskManagerPage({projectDetails = null}) {
  const { user } = useAuth();
  const { id: projectId } = useParams();
  const [tasks, setTasks] = useState([]);
  const [view, setView] = useState("kanban");
  const [selectedProject, setSelectedProject] = useState(projectDetails);

  const allProjects = projectId ? [] : useProjects();

  // Compute projects directly without state
  const projects = projectId
      ? (projectDetails ? [projectDetails] : [])
      : allProjects;

  // Single useEffect to handle projectDetails changes
  useEffect(() => {
    if (projectId && projectDetails) {
      setSelectedProject(projectDetails);
    }
  }, [projectId, projectDetails]);

  useEffect(() => {
    handleProjectChange(projectId);
  }, [projectId]);


  const [form, setForm] = useState({
    title: "",
    description: "",
    project_id: "",
    priority: "Medium",
    due_date: "",
    estimate_hours: 0,
    assignee_id: "",
  });

  const load = async () => {
    if (!user?.id) return;
    const res = await api
      .get(`${API}/tasks?project_id`, { params: { assignee_id: user.id, project_id: projectId } })
      .then((x) => x.data);
    setTasks(res);
  };

  useEffect(() => {
    load();
  }, [user]);

  const create = async (e) => {
    e.preventDefault();
    if (!form.title || !form.project_id) return;
    await api.post(`${API}/tasks`, {
      ...form,
      assignee_id: form.assignee_id || user.id,
    });
    setForm({
      title: "",
      description: "",
      project_id: projectId || "",
      priority: "Medium",
      due_date: "",
      estimate_hours: 0,
      assignee_id: "",
    });
    await load();
  };

  const update = async (tid, patch) => {
    await api.post(`${API}/tasks/${tid}`, patch);
    await load();
  };

  const remove = async (tid) => {
    if (window.confirm("Delete task?")) {
      await api.delete(`${API}/tasks/${tid}`);
      await load();
    }
  };

  const boardColumns = useMemo(
    () => [
      {
        key: "Backlog",
        title: "Backlog",
        items: tasks.filter((t) => t.status === "Backlog"),
      },
      {
        key: "In Progress",
        title: "In Progress",
        items: tasks.filter((t) => t.status === "In Progress"),
      },
      {
        key: "In Review",
        title: "In Review",
        items: tasks.filter((t) => t.status === "In Review"),
      },
      {
        key: "Done",
        title: "Done",
        items: tasks.filter((t) => {
          if (t.status !== "Done") return false;

          const updatedDate = new Date(t.updatedAt);
          const now = new Date();
          const sevenDaysAgo = new Date(now.setDate(now.getDate() - 5));

          return updatedDate >= sevenDaysAgo;
        }),
      },
    ],
    [tasks]
  );
  const onDropTask = async (tid, newStatus) => {
    await update(tid, { status: newStatus });
  };

  const handleProjectChange = (projectId) => {
    console.log("project change", projectId);
    const selectedProject = projects.find(p => p._id === projectId);

    setSelectedProject(selectedProject)
    setForm(prevForm => ({
      ...prevForm,
      project_id: projectId,
      stage_id: selectedProject?.current_stage_idx || null
    }));
  };

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <h1>Task Manager</h1>
        </div>
        <div className="row">
          <div className="tab-mini">
            <button
              className={view === "kanban" ? "active" : ""}
              onClick={() => setView("kanban")}
            >
              Kanban
            </button>
            <button
              className={view === "table" ? "active" : ""}
              onClick={() => setView("table")}
            >
              Table
            </button>
          </div>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Quick Add</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={create} className="form-grid">
            <div className="col-span-2">
              <Label>Title</Label>
              <Input
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                placeholder="Task title"
              />
            </div>
            <div>
              <Label>Project</Label>
              <Select
                  disabled={projectId}
                  value={form.project_id}
                  onValueChange={(id) => handleProjectChange(id)}
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Select project" />
                </SelectTrigger>
                <SelectContent className="max-h-72">
                  {projects.map((p) => (
                      <SelectItem key={p._id} value={p._id}>
                        {p.code} — {p.name} - {p.current_stage_idx}
                      </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {(user?.role === "ADMIN" || user?.role === "PM") && (
              <div>
                <Label>Assign To</Label>
                <Select
                    value={form.assignee_id || ""}
                    disabled={selectedProject === null || !selectedProject?.team?.length}
                    onValueChange={(v) => setForm({ ...form, assignee_id: v })}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Select member (optional)" />
                  </SelectTrigger>
                  <SelectContent className="max-h-72">
                    {selectedProject?.team && selectedProject.team.length > 0 ? (
                        selectedProject.team.map((u) => (
                            <SelectItem key={u.user_id} value={u.user_id}>
                              {u?.user?.name || "UNDEFINED"}
                            </SelectItem>
                        ))
                    ) : (
                        <div className="px-2 py-1.5 text-sm text-muted-foreground">
                          No users in team
                        </div>
                    )}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div>
              <Label>Priority</Label>
              <Select
                value={form.priority}
                onValueChange={(v) => setForm({ ...form, priority: v })}
              >
                <SelectTrigger className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Low">Low</SelectItem>
                  <SelectItem value="Medium">Medium</SelectItem>
                  <SelectItem value="High">High</SelectItem>
                  <SelectItem value="Critical">Critical</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Due Date</Label>
              <Input
                type="date"
                value={form.due_date}
                onChange={(e) => setForm({ ...form, due_date: e.target.value })}
              />
            </div>
            <div>
              <Label>Estimate (h)</Label>
              <Input
                type="number"
                value={form.estimate_hours}
                onChange={(e) =>
                  setForm({
                    ...form,
                    estimate_hours: Number(e.target.value || 0),
                  })
                }
              />
            </div>
            <div className="col-span-2">
              <Label>Description</Label>
              <textarea
                className="w-full border rounded-md p-2"
                rows={3}
                value={form.description}
                onChange={(e) =>
                  setForm({ ...form, description: e.target.value })
                }
                placeholder="Task description"
              />
            </div>
            <div className="col-span-2">
              <Button type="submit" className="w-full">
                Add Task
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      <Card className="mt-4">
        <CardHeader>
          <CardTitle>My Tasks</CardTitle>
        </CardHeader>

        <CardContent>
          {view === "kanban" ? (
            <KanbanBoard
              projects={projects}
              columns={boardColumns}
              onDropTask={onDropTask}
            />
          ) : (
            <Table className="mt-3">
              <TableHeader>
                <TableRow>
                  <TableHead>Project</TableHead>
                  <TableHead>Title</TableHead>
                  <TableHead>Assignee</TableHead>
                  <TableHead>Priority</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Due</TableHead>
                  <TableHead>Start Date</TableHead>
                  <TableHead>Est. (h)</TableHead>
                  <TableHead className="text-center">Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {tasks
                  .filter((t) => {
                    if (t.status !== "Done") return true; // always include non-Done

                    const createdDate = new Date(t.createdAt);
                    const now = new Date();
                    const sevenDaysAgo = new Date();
                    sevenDaysAgo.setDate(now.getDate() - 5);

                    return createdDate >= sevenDaysAgo; // only include Done if within 7 days
                  })
                  .map((t) => (
                    <TableRow key={t.id}>
                      <TableCell>
                        {projects.find((p) => p.id === t.project_id)?.code ||
                          t.project_id}
                      </TableCell>
                      <TableCell>{t.title}</TableCell>
                      <TableCell>{t.assignee_id?.name}</TableCell>
                      <TableCell>
                        <Select
                          value={t.priority}
                          onValueChange={(v) => update(t.id, { priority: v })}
                        >
                          <SelectTrigger className="w-[120px]">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="Low">Low</SelectItem>
                            <SelectItem value="Medium">Medium</SelectItem>
                            <SelectItem value="High">High</SelectItem>
                            <SelectItem value="Critical">Critical</SelectItem>
                          </SelectContent>
                        </Select>
                      </TableCell>
                      <TableCell>
                        <Select
                          value={t.status}
                          onValueChange={(v) => update(t.id, { status: v })}
                        >
                          <SelectTrigger className="w-[140px]">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="Backlog">Backlog</SelectItem>
                            <SelectItem value="In Progress">
                              In Progress
                            </SelectItem>
                            <SelectItem value="In Review">In Review</SelectItem>
                            <SelectItem value="Done">Done</SelectItem>
                          </SelectContent>
                        </Select>
                      </TableCell>
                      <TableCell>{fmtDate(t.due_date)}</TableCell>
                      <TableCell>{fmtDate(t.createdAt)}</TableCell>
                      <TableCell>{t.estimate_hours}</TableCell>
                      <TableCell>
                        <div>
                          <Button
                            size="sm"
                            className="ml-2"
                            onClick={() => remove(t.id)}
                          >
                            Delete
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                {tasks.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={9}>No tasks match filters.</TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

export default TaskManagerPage;
